import type {
  EligibilityOutcomeView,
  EligibilityReasonView,
  SupportedLocale,
} from '@stm/contracts';
import { translate, type TranslationKey } from '@stm/i18n';
import { profileStatusKeys } from './competitions';
import {
  classificationOf,
  registrationClassificationKeys,
  registrationModeKeys,
} from './competitionRegistrations';
import { evidenceTypeLabelKey } from './documentEvidence';

export const eligibilityOutcomeKeys: Readonly<Record<EligibilityOutcomeView, TranslationKey>> = {
  ELIGIBLE: 'eligibility.outcome.ELIGIBLE',
  INELIGIBLE: 'eligibility.outcome.INELIGIBLE',
  REVIEW_REQUIRED: 'eligibility.outcome.REVIEW_REQUIRED',
};

/** The colour that goes with the outcome; the outcome is always also written out. */
export const eligibilityOutcomeColors: Readonly<
  Record<EligibilityOutcomeView, 'success' | 'error' | 'warning'>
> = {
  ELIGIBLE: 'success',
  INELIGIBLE: 'error',
  REVIEW_REQUIRED: 'warning',
};

/** Every reason code the ice hockey adapter returns; its text may name `{detail}` values. */
export const eligibilityReasonKeys: Readonly<Record<string, TranslationKey>> = {
  PROFILE_MISSING: 'eligibility.reason.PROFILE_MISSING',
  PROFILE_NOT_ACTIVE: 'eligibility.reason.PROFILE_NOT_ACTIVE',
  PROFILE_NOT_VALID_ON_DATE: 'eligibility.reason.PROFILE_NOT_VALID_ON_DATE',
  BIRTH_DATE_AFTER_MATCH: 'eligibility.reason.BIRTH_DATE_AFTER_MATCH',
  REGISTRATION_MISSING: 'eligibility.reason.REGISTRATION_MISSING',
  REGISTRATION_NOT_VALID: 'eligibility.reason.REGISTRATION_NOT_VALID',
  LICENSE_REFERENCE_MISSING: 'eligibility.reason.LICENSE_REFERENCE_MISSING',
  CLASSIFICATION_NOT_PERMITTED: 'eligibility.reason.CLASSIFICATION_NOT_PERMITTED',
  AGE_BELOW_MINIMUM: 'eligibility.reason.AGE_BELOW_MINIMUM',
  EXCLUSIVE_HIGHER_REGISTRATION: 'eligibility.reason.EXCLUSIVE_HIGHER_REGISTRATION',
  BIRTH_DATE_ROUTE_NOT_DEFINED: 'eligibility.reason.BIRTH_DATE_ROUTE_NOT_DEFINED',
  AGE_EXCEPTION_NOT_SUPPORTED: 'eligibility.reason.AGE_EXCEPTION_NOT_SUPPORTED',
  BIRTH_DATE_OUTSIDE_PERMITTED_RANGE: 'eligibility.reason.BIRTH_DATE_OUTSIDE_PERMITTED_RANGE',
  DOCUMENT_MISSING: 'eligibility.reason.DOCUMENT_MISSING',
  DOCUMENT_NOT_VERIFIED: 'eligibility.reason.DOCUMENT_NOT_VERIFIED',
  DOCUMENT_EXPIRED: 'eligibility.reason.DOCUMENT_EXPIRED',
  DOCUMENT_NOT_YET_VALID: 'eligibility.reason.DOCUMENT_NOT_YET_VALID',
  SUSPENDED: 'eligibility.reason.SUSPENDED',
  ATTESTATION_MISSING: 'eligibility.reason.ATTESTATION_MISSING',
  ATTESTATION_DENIED: 'eligibility.reason.ATTESTATION_DENIED',
  TEAM_CHANGE_OUTSIDE_WINDOW: 'eligibility.reason.TEAM_CHANGE_OUTSIDE_WINDOW',
  EXTERNAL_HISTORY_INCOMPLETE: 'eligibility.reason.EXTERNAL_HISTORY_INCOMPLETE',
  DAILY_LIMIT_EXCEEDED: 'eligibility.reason.DAILY_LIMIT_EXCEEDED',
  ROLLING_WINDOW_LIMIT_EXCEEDED: 'eligibility.reason.ROLLING_WINDOW_LIMIT_EXCEEDED',
  PLAYOFF_PARTICIPATION_UNCONFIRMED: 'eligibility.reason.PLAYOFF_PARTICIPATION_UNCONFIRMED',
  PLAYOFF_PARTICIPATION_UNKNOWN: 'eligibility.reason.PLAYOFF_PARTICIPATION_UNKNOWN',
  PLAYOFF_PARTICIPATION_BELOW_MINIMUM: 'eligibility.reason.PLAYOFF_PARTICIPATION_BELOW_MINIMUM',
  ROSTER_RULES_NOT_DEFINED: 'eligibility.reason.ROSTER_RULES_NOT_DEFINED',
  ROSTER_SKATERS_MINIMUM_DEPENDS_ON_REVIEW:
    'eligibility.reason.ROSTER_SKATERS_MINIMUM_DEPENDS_ON_REVIEW',
  ROSTER_SKATERS_MINIMUM_NEEDS_PROMOTED_COUNT_REVIEW:
    'eligibility.reason.ROSTER_SKATERS_MINIMUM_NEEDS_PROMOTED_COUNT_REVIEW',
  ROSTER_SKATERS_BELOW_MINIMUM: 'eligibility.reason.ROSTER_SKATERS_BELOW_MINIMUM',
  ROSTER_SKATERS_ABOVE_MAXIMUM: 'eligibility.reason.ROSTER_SKATERS_ABOVE_MAXIMUM',
  ROSTER_GOALKEEPERS_MINIMUM_DEPENDS_ON_REVIEW:
    'eligibility.reason.ROSTER_GOALKEEPERS_MINIMUM_DEPENDS_ON_REVIEW',
  ROSTER_GOALKEEPERS_MINIMUM_NEEDS_PROMOTED_COUNT_REVIEW:
    'eligibility.reason.ROSTER_GOALKEEPERS_MINIMUM_NEEDS_PROMOTED_COUNT_REVIEW',
  ROSTER_GOALKEEPERS_BELOW_MINIMUM: 'eligibility.reason.ROSTER_GOALKEEPERS_BELOW_MINIMUM',
  ROSTER_GOALKEEPERS_ABOVE_MAXIMUM: 'eligibility.reason.ROSTER_GOALKEEPERS_ABOVE_MAXIMUM',
  ROSTER_PROMOTION_QUOTA_EXCEEDED: 'eligibility.reason.ROSTER_PROMOTION_QUOTA_EXCEEDED',
  ROSTER_HAS_INELIGIBLE_ENTRIES: 'eligibility.reason.ROSTER_HAS_INELIGIBLE_ENTRIES',
  ROSTER_HAS_ENTRIES_UNDER_REVIEW: 'eligibility.reason.ROSTER_HAS_ENTRIES_UNDER_REVIEW',
};

/** A detail value as the reader sees it: coded values are translated, the rest is shown as is. */
function detailText(
  locale: SupportedLocale,
  name: string,
  value: string | number | boolean,
): string {
  const text = String(value);
  if (name === 'documentType') {
    const key = evidenceTypeLabelKey(text);
    return key === null ? text : translate(locale, key);
  }
  if (name === 'registrationMode') {
    return text === 'native' || text === 'temporary_play_up' || text === 'exclusive_higher'
      ? translate(locale, registrationModeKeys[text])
      : text;
  }
  if (name === 'classification') {
    const classification = classificationOf(text);
    return classification === undefined
      ? text
      : translate(locale, registrationClassificationKeys[classification]);
  }
  if (name === 'status') {
    return text === 'draft' || text === 'active' || text === 'superseded'
      ? translate(locale, profileStatusKeys[text])
      : text;
  }
  if (typeof value === 'number') return value.toLocaleString(locale);
  return text;
}

/** The translated sentence of a reason with its details filled in. */
export function reasonText(locale: SupportedLocale, reason: EligibilityReasonView): string {
  const key = eligibilityReasonKeys[reason.code];
  if (key === undefined) {
    return translate(locale, 'eligibility.reason.unknown').replace('{code}', reason.code);
  }
  let text = translate(locale, key);
  for (const [name, value] of Object.entries(reason.detail)) {
    text = text.replaceAll(`{${name}}`, detailText(locale, name, value));
  }
  return text;
}

/** Reasons that decide the outcome first: not eligible, then review, each in the order given. */
export function reasonsInOrder(reasons: readonly EligibilityReasonView[]): EligibilityReasonView[] {
  return [
    ...reasons.filter((reason) => reason.outcome === 'INELIGIBLE'),
    ...reasons.filter((reason) => reason.outcome !== 'INELIGIBLE'),
  ];
}

export function eligibilityPath(base: string, matchId: string): string {
  return `${base}/matches/${encodeURIComponent(matchId)}/eligibility`;
}

/** The moment of the check, in the reader's language and their device's time zone. */
export function formatCheckedAt(instant: string, locale: SupportedLocale): string {
  const parsed = new Date(instant);
  if (Number.isNaN(parsed.getTime())) return instant;
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
    parsed,
  );
}
